/**
 * Admin sidebar layout — wraps every /admin/* page.
 */
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  LayoutDashboard, Building2, UserCog, FileText, AlertTriangle, LogOut, Tags,
} from 'lucide-react';
import { useAuthStore } from '@/lib/auth';

const NAV = [
  { to: '/admin', end: true, label: 'Platform Overview', icon: LayoutDashboard },
  { to: '/admin/brands', label: 'Brands', icon: Building2 },
  { to: '/admin/categories', label: 'Categories', icon: Tags },
  { to: '/admin/owners', label: 'Owners', icon: UserCog },
  { to: '/admin/audit', label: 'Audit Log', icon: FileText },
  { to: '/admin/event-failures', label: 'Event Failures', icon: AlertTriangle },
];

export default function AdminLayout() {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const params = useParams();
  const onLogout = () => {
    signOut();
    navigate('/login', { replace: true });
  };
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      <aside className="w-64 shrink-0 border-r border-white/5 bg-[#0E0E13] flex flex-col">
        <div className="px-6 py-6 border-b border-white/5 flex items-center gap-3">
          <img
            src="/onestamp-logo.png"
            alt="OneStamp"
            className="w-10 h-10 rounded-2xl shrink-0"
          />
          <div className="min-w-0">
            <div className="text-xs text-gold/85 uppercase tracking-wider font-semibold">
              OneStamp
            </div>
            <div className="text-sm font-semibold mt-0.5">Platform Console</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                'flex items-center gap-3 px-3 py-2 rounded-2xl text-sm transition-colors ' +
                (isActive
                  ? 'bg-gold/10 text-gold border border-gold/25 shadow-glow-gold-sm'
                  : 'text-white/70 hover:bg-white/5 hover:text-white border border-transparent')
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-white/5">
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-2xl text-sm text-white/70 hover:bg-white/5 hover:text-white"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 px-8 border-b border-white/5 flex items-center justify-between">
          <div className="text-sm text-white/60">
            Super Admin
            {params.brand_id ? (
              <span className="ml-2 px-2 py-1 rounded-md text-xs bg-gold/10 text-gold border border-gold/20">
                Drilling brand: {params.brand_id.slice(0, 8)}…
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium">{user?.name ?? user?.email}</div>
              <div className="text-xs text-gold">Super Admin</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 grid place-items-center text-gold font-semibold">
              {(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 px-8 py-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
